/**
 * MobileBottomNav Component
 * Fixed bottom navigation bar for mobile - thumb-first design
 * Limited to 5 primary sections with icons + short labels
 */

import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, LayoutGroup } from "framer-motion";
import {
  HomeIcon,
  BuildingOfficeIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon as HomeSolid,
  BuildingOfficeIcon as BuildingOfficeSolid,
  WrenchScrewdriverIcon as WrenchScrewdriverSolid,
  CreditCardIcon as CreditCardSolid,
  UserCircleIcon as UserCircleSolid,
} from "@heroicons/react/24/solid";

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconActive: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: "home",
    label: "Home",
    href: "/tenant",
    icon: HomeIcon,
    iconActive: HomeSolid,
  },
  {
    id: "units",
    label: "Units",
    href: "/tenant/units",
    icon: BuildingOfficeIcon,
    iconActive: BuildingOfficeSolid,
  },
  {
    id: "maintenance",
    label: "Repairs",
    href: "/tenant/maintenance",
    icon: WrenchScrewdriverIcon,
    iconActive: WrenchScrewdriverSolid,
  },
  {
    id: "invoices",
    label: "Bills",
    href: "/tenant/invoices",
    icon: CreditCardIcon,
    iconActive: CreditCardSolid,
  },
  {
    id: "profile",
    label: "Profile",
    href: "/tenant/profile",
    icon: UserCircleIcon,
    iconActive: UserCircleSolid,
  },
];

const MobileBottomNav: React.FC = () => {
  const location = useLocation();

  const getActiveIndex = useMemo(() => {
    for (let i = 0; i < NAV_ITEMS.length; i++) {
      const item = NAV_ITEMS[i];
      if (item.href === "/tenant") {
        if (location.pathname === "/tenant" || location.pathname === "/tenant/") {
          return i;
        }
      } else if (
        location.pathname === item.href ||
        location.pathname.startsWith(`${item.href}/`)
      ) {
        return i;
      }
    }
    return 0;
  }, [location.pathname]);

  const isActive = (index: number) => index === getActiveIndex;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-white lg:hidden"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Shadow overlay for elevated effect */}
      <div className="absolute inset-x-0 -top-4 h-4 bg-gradient-to-t from-white/80 to-transparent pointer-events-none" />
      
      <LayoutGroup>
        <div className="relative flex items-end justify-around h-16 px-4 bg-white rounded-t-3xl shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          {NAV_ITEMS.map((item, index) => {
            const active = isActive(index);
            const Icon = item.icon;
            const IconActive = item.iconActive;

            return (
              <Link
                key={item.id}
                to={item.href}
                className="relative flex flex-col items-center justify-end flex-1 h-full min-w-0 pb-2"
                aria-current={active ? "page" : undefined}
              >
                {/* Sliding active circle with icon inside */}
                {active && (
                  <motion.div
                    layoutId="activeNavCircle"
                    className="absolute -top-4 w-12 h-12 bg-purple-600 rounded-full shadow-lg shadow-purple-600/25 flex items-center justify-center"
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 32,
                    }}
                  >
                    <IconActive className="w-5 h-5 text-white" />
                  </motion.div>
                )}

                {/* Inactive icon */}
                {!active && (
                  <motion.div
                    className="flex items-center justify-center w-10 h-10 text-gray-400"
                    whileTap={{ scale: 0.9 }}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.div>
                )}

                {/* Label - visible for all tabs */}
                <span
                  className={`text-[10px] font-medium ${
                    active ? "mt-5 text-purple-600" : "text-gray-400"
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </LayoutGroup>
    </nav>
  );
};

export default MobileBottomNav;

